import { getApiHeaders } from '../utils/apiHeaders';
import { toSnakeCase } from '../utils/caseConverter';
import {
  TelecallingInteraction,
  TelecallingQueueResponse,
  TelecallingSummary,
} from '../types/Telecalling';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export async function uploadTelecallingLeads(file: File) {
  const form = new FormData();
  form.append('file', file);

  const res = await fetch(`${API_BASE_URL}/api/telecalling/leads/import`, {
    method: 'POST',
    headers: getApiHeaders('none'),
    body: form,
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to upload leads: ${errorText}`);
  }
  return res.json();
}

export async function assignTelecallingCalls(assignments: Array<{ userId: number; callCount: number }>) {
  const res = await fetch(`${API_BASE_URL}/api/telecalling/assignments`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify(toSnakeCase({ assignments })),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to assign calls: ${errorText}`);
  }
  return res.json();
}

export async function fetchTelecallingSummary(params?: {
  date?: string;
  from?: string;
  to?: string;
  userId?: number;
}): Promise<TelecallingSummary> {
  const url = new URL(`${API_BASE_URL}/api/telecalling/summary`);
  if (params?.date) url.searchParams.set('date', params.date);
  if (params?.from) url.searchParams.set('from', params.from);
  if (params?.to) url.searchParams.set('to', params.to);
  if (params?.userId) url.searchParams.set('user_id', String(params.userId));

  const res = await fetch(url.toString(), {
    headers: getApiHeaders('none'),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to load telecalling summary: ${errorText}`);
  }
  const data: any = await res.json();
  const totals = data.totals || data.summary || data.stats || {};
  const byUser = data.byUser || data.by_user || data.users || data.items || [];
  return {
    totals: {
      assigned: Number(totals.assigned || 0),
      completed: Number(totals.completed || 0),
      invalidNumber: Number(totals.invalid_number || totals.invalidNumber || 0),
      notPicked: Number(totals.not_picked || totals.notPicked || 0),
      notInterested: Number(totals.not_interested || totals.notInterested || 0),
      interested: Number(totals.interested || 0),
      converted: Number(totals.converted || 0),
    },
    byUser: Array.isArray(byUser)
      ? byUser.map((item: any) => ({
          userId: Number(item.user_id || item.userId || 0),
          assigned: Number(item.assigned || 0),
          completed: Number(item.completed || 0),
          invalidNumber: Number(item.invalid_number || item.invalidNumber || 0),
          notPicked: Number(item.not_picked || item.notPicked || 0),
          notInterested: Number(item.not_interested || item.notInterested || 0),
          interested: Number(item.interested || 0),
          converted: Number(item.converted || 0),
        }))
      : [],
  };
}

export async function fetchTelecallingQueue(limit?: number): Promise<TelecallingQueueResponse> {
  const url = new URL(`${API_BASE_URL}/api/telecalling/queue`);
  if (limit) url.searchParams.set('limit', String(limit));

  const res = await fetch(url.toString(), {
    headers: getApiHeaders('none'),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to load telecalling queue: ${errorText}`);
  }
  const data: any = await res.json();
  const normalizeItem = (item: any) => (item && item.lead ? item : { lead: item });
  if (Array.isArray(data)) {
    return { items: data.map(normalizeItem) };
  }
  if (Array.isArray(data.items)) {
    return { items: data.items.map(normalizeItem), total: data.total };
  }
  if (Array.isArray(data.leads)) {
    return { items: data.leads.map((lead: any) => ({ lead })), total: data.total };
  }
  return { items: [] };
}

export async function logTelecallingInteraction(
  leadId: number | string,
  payload: TelecallingInteraction
) {
  const res = await fetch(`${API_BASE_URL}/api/telecalling/leads/${leadId}/interactions`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify(toSnakeCase(payload)),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to log interaction: ${errorText}`);
  }
  return res.json();
}
